// src/App.js
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import { createHashRouter, RouterProvider } from 'react-router-dom';
import Welcome from './components/Welcome';
import Dashboard from './components/Dashboard';
import ExpenseList from './components/ExpenseList';
import NotFound from './components/NotFound';
import TermsAndConditions from './components/TermsAndConditions';
import Statistics from './components/Statistics/Statistics';
import Register from './components/Register';
import PrivacyPolicy from './components/PrivacyPolicy';
import Achievements from './components/Achievements';
import VersionChecker from './components/VersionChecker'; // AGGIUNTA per il controllo versione
import TelegramBrowserWarning from './components/TelegramBrowserWarning'; // AGGIUNTA per l'avviso del browser Telegram
import {
    ThemeProvider,
    createTheme,
    CssBaseline,
    Box,
    CircularProgress
} from '@mui/material';
import '@fontsource/poppins';
import useBackgroundImage from './hooks/useBackgroundImage';
import './index.css'; // Importa il file CSS globale

// Soluzione estrema: Override di appendChild
//window.__originalAppendChild = Node.prototype.appendChild;
//Node.prototype.appendChild = function(node) {
//    if (node.tagName === 'DIV' && node.classList?.contains('iubenda-cs-preferences-link')) {
//        console.log('Bloccato elemento fluttuante di Iubenda');
//        return null; // Blocca l'aggiunta del nodo
//    }
//    return window.__originalAppendChild.call(this, node);
//};

const BackgroundWrapper = ({ children }) => {
    const excludeBackgroundRoutes = ['/expense-list'];
    const { pathname } = window.location;
    const shouldShowBackground = !excludeBackgroundRoutes.includes(pathname);
    const { backgroundImage, isLoading } = useBackgroundImage(shouldShowBackground);
    const theme = useTheme();

    // Observer per intercettare elementi dinamici
    useEffect(() => {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                Array.from(mutation.addedNodes).forEach(node => {
                    if (node.classList?.contains('iubenda-cs-preferences-link')) {
                        node.style.cssText = `
                            opacity: 0 !important;
                            visibility: hidden !important;
                            position: fixed !important;
                            left: -9999px !important;
                            top: -9999px !important;
                            z-index: -1000 !important;
                            pointer-events: none !important;
                        `;
                    }
                });
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        return () => observer.disconnect();
    }, []);

    return (
        <Box
            sx={{
                position: 'relative',
                backgroundImage,
                ...(backgroundImage !== 'none' && {
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    backgroundAttachment: 'fixed',
                }),
                minHeight: shouldShowBackground ? '100vh' : 'auto',
                transition: 'background-color 0.3s ease, backdrop-filter 0.3s ease',
                '& .iubenda-cs-preferences-link': {
                    opacity: '0 !important',
                    visibility: 'hidden !important',
                    left: '-9999px !important',
                    top: '-9999px !important',
                    zIndex: '-1000 !important'
                }
            }}
        >
            {/* Overlay per la modalità scura */}
            {shouldShowBackground && theme.palette.mode === 'dark' && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        backdropFilter: 'blur(15px)',
                        transition: 'background-color 0.3s ease, backdrop-filter 0.3s ease',
                        zIndex: 1,
                    }}
                />
            )}

            {/* Indicatore di caricamento */}
            {isLoading && shouldShowBackground && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                        backdropFilter: 'blur(5px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2,
                    }}
                >
                    <CircularProgress />
                </Box>
            )}

            <Box sx={{ position: 'relative', zIndex: 3 }}>
                {children}
            </Box>
        </Box>
    );
};

const App = () => {
    const [mode, setMode] = useState(() => {
        const savedMode = localStorage.getItem('mode');
        return savedMode === 'dark' ? 'dark' : 'light';
    });

    useEffect(() => {
        localStorage.setItem('mode', mode);
    }, [mode]);

    const toggleMode = useCallback(() => {
        setMode(prev => (prev === 'light' ? 'dark' : 'light'));
    }, []);

    // Integrazione Iubenda
    useEffect(() => {
        const initIubenda = () => {
            if (!window._iub) {
                window._iub = window._iub || [];
                window._iub.csConfiguration = {
                    "siteId": 3926384,
                    "cookiePolicyId": 93756875,
                    "lang": "it",
                    "storage": { 
                        "useSiteId": true,
                        "storeInFirstParty": true 
                    },
                    "banner": {
                        "position": "bottom",
                        "floatingPreferencesButtonDisplay": false,
                        "showPurposesTab": false,
                        "acceptButtonDisplay": true,
                        "customizeButtonDisplay": false,
                        "closeButtonDisplay": false
                    },
                    "callback": {
                        onConsentGiven: () => {
                            console.log('Consenso dato');
                        }
                    }
                };

                const loadScript = () => {
                    const script = document.createElement('script');
                    script.src = '//cdn.iubenda.com/cs/iubenda_cs.js?v=1.75.0';
                    script.async = true;
                    script.charset = 'UTF-8';
                    document.head.appendChild(script);
                };

                // Delay per evitare conflitti con altri script
                setTimeout(loadScript, 500);
            }
        };

        const handleLoad = () => {
            initIubenda();
            // Forza il ripristino delle impostazioni
            if (window._iub && window._iub.cs) {
                window._iub.cs.reset();
            }
        };

        if (document.readyState === 'complete') {
            handleLoad();
        } else {
            window.addEventListener('load', handleLoad);
        }

        return () => {
            window.removeEventListener('load', handleLoad);
        };
    }, []);

    const theme = useMemo(() =>
        createTheme({
            palette: {
                mode,
                ...(mode === 'light'
                    ? {
                        primary: { main: '#000000' },
                        secondary: { main: '#000000' },
                        background: { default: '#FFFFFF', paper: '#FFFFFF' },
                        text: { primary: '#000000', secondary: '#333333' },
                    }
                    : {
                        primary: { main: '#FFFFFF' },
                        secondary: { main: '#FFFFFF' },
                        background: { default: '#121212', paper: '#1E1E1E' },
                        text: { primary: '#FFFFFF', secondary: '#CCCCCC' },
                    }),
            },
            typography: {
                fontFamily: 'Poppins, Roboto, Arial, sans-serif',
                h1: { fontWeight: 400 },
                h2: { fontWeight: 400 },
                h3: { fontWeight: 400 },
                body1: { fontWeight: 300 },
                body2: { fontWeight: 300 },
            },
            shape: { borderRadius: 0 },
            shadows: Array(25).fill('none'),
            components: {
                MuiCssBaseline: {
                    styleOverrides: {
                        body: {
                            WebkitFontSmoothing: 'none',
                            letterSpacing: '0.5px',
                            margin: 0,
                            padding: 0,
                        },
                    },
                },
                MuiPaper: {
                    styleOverrides: {
                        root: {
                            border: '1px solid',
                            borderColor: mode === 'light' ? '#000' : '#fff',
                            backgroundColor: mode === 'light' ? '#FFFFFF' : '#1E1E1E',
                        },
                    },
                },
                MuiButton: {
                    styleOverrides: {
                        root: {
                            backgroundColor: 'transparent',
                            border: '1px solid',
                            borderColor: mode === 'light' ? '#000' : '#fff',
                            color: mode === 'light' ? '#000' : '#fff',
                            borderRadius: 0,
                            textTransform: 'none',
                            ':hover': {
                                backgroundColor: mode === 'light' ? '#F0F0F0' : '#333333',
                            },
                        },
                    },
                },
                MuiTextField: {
                    styleOverrides: {
                        root: {
                            border: '1px solid',
                            borderColor: mode === 'light' ? '#000' : '#fff',
                            borderRadius: 0,
                            backgroundColor: 'transparent',
                            '.MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: 'transparent' },
                                '&:hover fieldset': { borderColor: mode === 'light' ? '#000' : '#fff' },
                                '&.Mui-focused fieldset': { borderColor: mode === 'light' ? '#000' : '#fff' },
                            },
                            input: {
                                color: mode === 'light' ? '#000' : '#fff',
                                backgroundColor: 'transparent',
                            }
                        },
                    },
                },
                MuiIconButton: {
                    styleOverrides: {
                        root: {
                            border: '1px solid',
                            borderColor: mode === 'light' ? '#000' : '#fff',
                            borderRadius: 0,
                            backgroundColor: 'transparent',
                            '&:hover': { backgroundColor: mode === 'light' ? '#F0F0F0' : '#333333' },
                        },
                    },
                },
                MuiAppBar: {
                    styleOverrides: {
                        root: {
                            backgroundColor: mode === 'light' ? '#FFFFFF' : '#000000',
                            color: mode === 'light' ? '#000' : '#fff',
                            borderBottom: '1px solid',
                            borderColor: mode === 'light' ? '#000' : '#fff',
                            boxShadow: 'none',
                        },
                    },
                },
                MuiDrawer: {
                    styleOverrides: {
                        paper: {
                            backgroundColor: mode === 'light' ? '#FFFFFF' : '#000000',
                            color: mode === 'light' ? '#000' : '#fff',
                            borderRight: '1px solid',
                            borderColor: mode === 'light' ? '#000' : '#fff',
                        },
                    },
                },
                MuiListItem: {
                    styleOverrides: {
                        root: {
                            '&:hover': { backgroundColor: mode === 'light' ? '#F0F0F0' : '#333333' },
                        },
                    },
                },
                MuiBottomNavigation: {
                    styleOverrides: {
                        root: {
                            backgroundColor: mode === 'light' ? '#FFFFFF' : '#000000',
                            borderTop: '1px solid',
                            borderColor: mode === 'light' ? '#000' : '#fff',
                            backdropFilter: 'none',
                        },
                    },
                },
                MuiBottomNavigationAction: {
                    styleOverrides: {
                        root: {
                            color: mode === 'light' ? '#333' : '#CCC',
                            '&.Mui-selected': { color: mode === 'light' ? '#000' : '#fff' },
                        },
                    },
                },
            },
        }),
        [mode]
    );

    const router = useMemo(() =>
        createHashRouter([
            {
                path: '/',
                element: (
                    <BackgroundWrapper>
                        <Welcome mode={mode} toggleMode={toggleMode} />
                    </BackgroundWrapper>
                ),
            },
            {
                path: '/terms-and-conditions',
                element: (
                    <BackgroundWrapper>
                        <TermsAndConditions mode={mode} toggleMode={toggleMode} />
                    </BackgroundWrapper>
                ),
            },
            {
                path: '/dashboard',
                element: (
                    <BackgroundWrapper>
                        <Dashboard mode={mode} toggleMode={toggleMode} />
                    </BackgroundWrapper>
                ),
            },
            {
                path: '/expense-list',
                element: (
                    <BackgroundWrapper>
                        <ExpenseList mode={mode} toggleMode={toggleMode} />
                    </BackgroundWrapper>
                ),
            },
            {
                path: '/statistics',
                element: (
                    <BackgroundWrapper>
                        <Statistics mode={mode} toggleMode={toggleMode} />
                    </BackgroundWrapper>
                ),
            },
            {
                path: '/register',
                element: (
                    <BackgroundWrapper>
                        <Register mode={mode} toggleMode={toggleMode} />
                    </BackgroundWrapper>
                ),
            },
            {
                path: '/privacy',
                element: (
                    <BackgroundWrapper>
                        <PrivacyPolicy />
                    </BackgroundWrapper>
                ),
            },
            {
                path: '/achievements',
                element: (
                    <BackgroundWrapper>
                        <Achievements />
                    </BackgroundWrapper>
                ),
            },
            {
                path: '*',
                element: (
                    <BackgroundWrapper>
                        <NotFound mode={mode} toggleMode={toggleMode} />
                    </BackgroundWrapper>
                ),
            },
        ], {
            future: {
                v7_startTransition: true,
                v7_relativeSplatPath: true,
                v7_fetcherPersist: true,
                v7_normalizeFormMethod: true,
                v7_partialHydration: true,
                v7_skipActionErrorRevalidation: true,
            },
        }),
        [mode, toggleMode]
    );

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            {/* Aggiunto VersionChecker per controllare automaticamente la versione */}
            <VersionChecker />
            {/* Aggiunto TelegramBrowserWarning per avvisare gli utenti se usano il browser interno di Telegram */}
            {/* Nota: il controllo ora utilizza window.TelegramWebview e TelegramWebviewProxy */}
            <TelegramBrowserWarning />
            <RouterProvider router={router} />
        </ThemeProvider>
    );
};

export default App;
